(() => {
  "use strict";

  const NS = "http://www.w3.org/2000/svg";
  const COLOURS = {
    w: ["#ffffff", "#dfe3e6"], y: ["#ffe20b", "#e6b800"],
    r: ["#f34b43", "#c81e2a"], o: ["#ff912e", "#e75b09"],
    g: ["#25c578", "#078b4e"], b: ["#3987f5", "#1558c7"],
    p: ["#a36be2", "#7137b8"], c: ["#59dce7", "#20aebc"],
    m: ["#f48dbc", "#d94f91"], l: ["#9be044", "#65aa1d"],
    a: ["#e2c78b", "#b79151"], d: ["#2855a6", "#102e69"],
    x: ["#989da5", "#686e77"]
  };
  const VALID = new RegExp(`[^${Object.keys(COLOURS).join("")}]`, "g");
  const P = (x, y) => ({ x, y });
  const mix = (a, b, t) => P(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
  const points = (items) => items.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");

  function svgElement(name, attrs = {}) {
    const node = document.createElementNS(NS, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  }

  function normaliseState(value) {
    return String(value || "").toLowerCase().replace(VALID, "");
  }

  function parseDimensions(value, fallbackSize = 3) {
    const match = String(value || "").trim().toLowerCase().match(/^(\d+)\s*[x×]\s*(\d+)\s*[x×]\s*(\d+)$/);
    if (match) return match.slice(1).map((part) => Math.max(1, Math.min(99, Number(part))));
    const size = Math.max(1, Math.min(99, Number(fallbackSize) || 3));
    return [size, size, size];
  }

  function expectedCount({ puzzle = "cube", diagram = "three-d", size = 3, dimensions = "" }) {
    const [width, height, depth] = parseDimensions(dimensions, size);
    if (puzzle === "cube") return diagram === "last-layer" ? width * depth + 2 * width + 2 * depth : width * depth + width * height + depth * height;
    if (puzzle === "pyraminx") return diagram === "two-face" ? 18 : 27;
    if (puzzle === "megaminx") return diagram === "last-layer" ? 26 : 66;
    return { skewb: 15, fto: 36 }[puzzle] || 0;
  }

  function defaultState({ puzzle = "cube", diagram = "three-d", size = 3, dimensions = "" }) {
    const [width, height, depth] = parseDimensions(dimensions, size);
    if (puzzle === "cube") {
      if (diagram === "last-layer") return "y".repeat(width * depth) + "r".repeat(width) + "b".repeat(depth) + "o".repeat(width) + "g".repeat(depth);
      return "y".repeat(width * depth) + "r".repeat(width * height) + "g".repeat(depth * height);
    }
    if (puzzle === "pyraminx") return diagram === "two-face" ? "b".repeat(9) + "g".repeat(9) : "b".repeat(9) + "g".repeat(9) + "y".repeat(9);
    if (puzzle === "skewb") return "y".repeat(5) + "r".repeat(5) + "g".repeat(5);
    if (puzzle === "megaminx") return diagram === "last-layer" ? "y".repeat(11) + "x".repeat(15) : "a".repeat(11) + "c".repeat(11) + "y".repeat(11) + "g".repeat(11) + "r".repeat(11) + "m".repeat(11);
    if (puzzle === "fto") return "y".repeat(9) + "r".repeat(9) + "g".repeat(9) + "b".repeat(9);
    return "";
  }

  function cell(q, row, column, rows, columns = rows) {
    const a = mix(q[0], q[1], column / columns), b = mix(q[0], q[1], (column + 1) / columns);
    const d = mix(q[3], q[2], column / columns), e = mix(q[3], q[2], (column + 1) / columns);
    return [mix(a, d, row / rows), mix(b, e, row / rows), mix(b, e, (row + 1) / rows), mix(a, d, (row + 1) / rows)];
  }

  function stripCell(q, index, count, vertical) {
    if (vertical) {
      return [mix(q[0], q[3], index / count), mix(q[1], q[2], index / count), mix(q[1], q[2], (index + 1) / count), mix(q[0], q[3], (index + 1) / count)];
    }
    return [mix(q[0], q[1], index / count), mix(q[0], q[1], (index + 1) / count), mix(q[3], q[2], (index + 1) / count), mix(q[3], q[2], index / count)];
  }

  function inset(poly, amount) {
    const centre = poly.reduce((sum, p) => P(sum.x + p.x, sum.y + p.y), P(0, 0));
    centre.x /= poly.length; centre.y /= poly.length;
    return poly.map((p) => P(centre.x + (p.x - centre.x) * (1 - amount), centre.y + (p.y - centre.y) * (1 - amount)));
  }

  function trianglePieces([apex, left, right]) {
    const out = [];
    const point = (row, k) => row ? mix(mix(apex, left, row / 3), mix(apex, right, row / 3), k / row) : apex;
    for (let row = 0; row < 3; row++) {
      for (let k = 0; k <= row; k++) out.push([point(row, k), point(row + 1, k), point(row + 1, k + 1)]);
      for (let k = 0; k < row; k++) out.push([point(row, k), point(row, k + 1), point(row + 1, k + 1)]);
    }
    return out;
  }

  function regular(cx, cy, radius, sides, rotation = -Math.PI / 2) {
    return Array.from({ length: sides }, (_, i) => P(cx + Math.cos(rotation + i * Math.PI * 2 / sides) * radius, cy + Math.sin(rotation + i * Math.PI * 2 / sides) * radius));
  }

  function pentagonPieces(q) {
    const centre = q.reduce((sum, p) => P(sum.x + p.x, sum.y + p.y), P(0, 0));
    centre.x /= 5; centre.y /= 5;
    const inner = q.map((p) => mix(centre, p, 0.44)), parts = [inner];
    for (let i = 0; i < 5; i++) {
      const previous=(i+4)%5,next=(i+1)%5;
      const incoming=mix(q[previous],q[i],.68),outgoing=mix(q[i],q[next],.32);
      parts.push([q[i],outgoing,inner[i],incoming]);
      parts.push([outgoing,mix(q[i],q[next],.68),inner[next],inner[i]]);
    }
    return parts;
  }

  function makeSvg(config, onPieceClick) {
    const puzzle = config.puzzle || "cube", diagram = config.diagram || "three-d";
    const [width, height, depth] = parseDimensions(config.dimensions, config.size), view = config.view || "right";
    const gap = Math.max(0, Math.min(0.14, Number(config.gap ?? 6) / 100));
    const state = normaliseState(config.state);
    const svg = svgElement("svg", { viewBox: "0 0 500 550", role: "img", "aria-label": config.label || `${puzzle} diagram` });
    svg.classList.add("pd-svg");

    const defs = svgElement("defs");
    defs.innerHTML = '<filter id="pd-soft"><feGaussianBlur stdDeviation="9"/></filter><style>.pd-face-base{fill:#151922;stroke:#151922;stroke-width:11;stroke-linejoin:round}.pd-piece{stroke:#181c24;stroke-width:5;stroke-linejoin:round}</style>';
    Object.entries(COLOURS).forEach(([key, pair]) => {
      const gradient = svgElement("linearGradient", { id: `pd-${key}`, x1: "0", y1: "0", x2: "1", y2: "1" });
      gradient.append(svgElement("stop", { offset: "0", "stop-color": pair[0] }), svgElement("stop", { offset: "1", "stop-color": pair[1] }));
      defs.append(gradient);
    });
    svg.append(defs, svgElement("ellipse", { cx: 250, cy: 505, rx: 178, ry: 18, fill: "#172033", "fill-opacity": ".15", filter: "url(#pd-soft)" }));

    const addBase = (poly) => svg.append(svgElement("polygon", { points: points(poly), class: "pd-face-base" }));
    const addPiece = (poly, index, label, useInset = true, insetAmount = gap) => {
      const shape = svgElement("polygon", { points: points(useInset ? inset(poly, insetAmount) : poly), fill: `url(#pd-${state[index]})`, class: "pd-piece", tabindex: onPieceClick ? "0" : "-1", "aria-label": label });
      if (onPieceClick) {
        const activate = () => onPieceClick(index);
        shape.addEventListener("click", activate);
        shape.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); activate(); } });
      }
      svg.append(shape);
    };

    const cubeFaces = () => {
      const unit = Math.min(270 / width, 285 / height, 155 / (depth * 0.46), 105 / (depth * 0.34));
      const frontWidth = width * unit, frontHeight = height * unit;
      const dx = depth * unit * 0.46, dy = depth * unit * 0.34;
      const totalWidth = frontWidth + dx, x = (500 - totalWidth) / 2 + (view === "left" ? dx : 0);
      const y = 135 + dy / 2;
      const front = [P(x,y),P(x+frontWidth,y),P(x+frontWidth,y+frontHeight),P(x,y+frontHeight)];
      if (view === "left") {
        const backLeft = P(x-dx,y-dy), backRight = P(x+frontWidth-dx,y-dy);
        return [[backLeft,backRight,front[1],front[0]],front,[backLeft,front[0],front[3],P(x-dx,y+frontHeight-dy)]];
      }
      const backLeft = P(x+dx,y-dy), backRight = P(x+frontWidth+dx,y-dy);
      return [[front[0],front[1],backRight,backLeft],front,[front[1],backRight,P(x+frontWidth+dx,y+frontHeight-dy),front[2]]];
    };

    const renderCube = () => {
      let index = 0; const faces = cubeFaces(); faces.forEach(addBase);
      const grids = [[depth,width],[height,width],[height,depth]];
      faces.forEach((q, face) => { const [rows,columns] = grids[face]; for (let row = 0; row < rows; row++) for (let column = 0; column < columns; column++) addPiece(cell(q,row,column,rows,columns),index++,`face ${face + 1}, row ${row + 1}, column ${column + 1}`); });
    };
    const renderLastLayer = () => {
      const unit = Math.min(250 / width, 250 / depth), faceWidth = width * unit, faceHeight = depth * unit;
      const x = (500-faceWidth)/2, y = (500-faceHeight)/2, thickness = 34;
      const face = [P(x,y),P(x+faceWidth,y),P(x+faceWidth,y+faceHeight),P(x,y+faceHeight)];
      const strips = [[P(x,y-thickness),P(x+faceWidth,y-thickness),P(x+faceWidth,y),P(x,y)],[P(x+faceWidth,y),P(x+faceWidth+thickness,y),P(x+faceWidth+thickness,y+faceHeight),P(x+faceWidth,y+faceHeight)],[P(x,y+faceHeight),P(x+faceWidth,y+faceHeight),P(x+faceWidth,y+faceHeight+thickness),P(x,y+faceHeight+thickness)],[P(x-thickness,y),P(x,y),P(x,y+faceHeight),P(x-thickness,y+faceHeight)]];
      addBase(face); let index = 0;
      for (let row = 0; row < depth; row++) for (let column = 0; column < width; column++) addPiece(cell(face,row,column,depth,width),index++,`upper face row ${row+1}, column ${column+1}`);
      strips.forEach((q, faceIndex) => { const count=faceIndex===0||faceIndex===2?width:depth;for(let k=0;k<count;k++){const position=faceIndex>=2?count-1-k:k;addPiece(stripCell(q,position,count,faceIndex===1||faceIndex===3),index++,`side strip ${faceIndex+1}, piece ${k+1}`,false);} });
    };
    const renderLegacyPyraminx = () => {
      const apex=P(250,48),lower=P(250,472),left=P(42,365),right=P(458,365);
      const faces=[[apex,left,lower],[apex,lower,right]];let index=0;
      faces.forEach(addBase);
      faces.forEach((q,face)=>trianglePieces(q).forEach((poly,piece)=>addPiece(poly,index++,`${face?"right":"left"} face, piece ${piece+1}`)));
    };
    const renderPyraminx = () => {
      const top=P(250,42),left=P(39,430),right=P(461,430),centre=P(view==="left"?175:325,310);
      const screenLeft=[top,left,centre],screenRight=[top,centre,right],lower=[left,right,centre];
      const faces=view==="left"?[screenRight,screenLeft,lower]:[screenLeft,screenRight,lower];let index=0;
      faces.forEach(addBase);
      faces.forEach((q,face)=>trianglePieces(q).forEach((poly,piece)=>addPiece(poly,index++,`${["front","side","lower"][face]} face, piece ${piece+1}`)));
    };
    const renderSkewb = () => {
      let index = 0; cubeFaces().forEach((q, face) => { addBase(q); const mids = q.map((p, j) => mix(p, q[(j + 1) % 4], 0.5)); for (let j = 0; j < 4; j++) addPiece([q[j],mids[j],mids[(j+3)%4]],index++,`face ${face+1}, corner ${j+1}`); addPiece(mids,index++,`face ${face+1}, centre`); });
    };
    const renderLegacyMegaminx = () => {
      const centre=P(250,267),q=regular(centre.x,centre.y,126,5),outer=q.map((p)=>P(centre.x+(p.x-centre.x)*1.43,centre.y+(p.y-centre.y)*1.43));
      const strips=q.map((p,j)=>[q[j],q[(j+1)%5],outer[(j+1)%5],outer[j]]);let index=0;
      strips.forEach(addBase);addBase(q);
      const minxGap=Math.min(gap,.025);
      pentagonPieces(q).forEach((poly,piece)=>addPiece(poly,index++,`last-layer face, piece ${piece+1}`,true,minxGap));
      strips.forEach((strip,face)=>{for(let k=0;k<3;k++)addPiece(stripCell(strip,k,3,false),index++,`adjacent face ${face+1}, piece ${k+1}`,true,minxGap);});
    };
    const renderMegaminx = () => {
      const centre=P(250,280);
      const project=(point,origin,scaleY=0.82)=>P(point.x,origin.y+(point.y-origin.y)*scaleY);
      const front=regular(centre.x,centre.y,109,5,Math.PI/2).map((point)=>project(point,centre,.9));
      const outerCentre=P(250,274),outer=regular(outerCentre.x,outerCentre.y,210,10,Math.PI/2).map((point)=>project(point,outerCentre,.86));
      const surrounding=front.map((_,i)=>[front[i],front[(i+1)%5],outer[(i*2+2)%10],outer[(i*2+1)%10],outer[i*2]]);
      const faces=[front,...surrounding];let index=0;
      surrounding.forEach(addBase);addBase(front);
      const minxGap=Math.min(gap,.022);
      faces.forEach((q,face)=>pentagonPieces(q).forEach((poly,piece)=>addPiece(poly,index++,`${face?`surrounding face ${face}`:"front face"}, piece ${piece+1}`,true,minxGap)));
    };
    const renderFto = () => {
      const topLeft=P(66,86),topRight=P(434,86),bottomRight=P(434,454),bottomLeft=P(66,454),centre=P(250,270);
      const faces=[[topRight,topLeft,centre],[topRight,centre,bottomRight],[bottomRight,centre,bottomLeft],[topLeft,bottomLeft,centre]];let index=0;
      faces.forEach(addBase);
      faces.forEach((q,face)=>trianglePieces(q).forEach((poly,piece)=>addPiece(poly,index++,`${["top","right","bottom","left"][face]} face, piece ${piece+1}`)));
      svg.append(svgElement("path",{d:`M ${topLeft.x} ${topLeft.y} L ${topRight.x} ${topRight.y} L ${bottomRight.x} ${bottomRight.y} L ${bottomLeft.x} ${bottomLeft.y} Z M ${topLeft.x} ${topLeft.y} L ${centre.x} ${centre.y} M ${topRight.x} ${topRight.y} L ${centre.x} ${centre.y} M ${bottomRight.x} ${bottomRight.y} L ${centre.x} ${centre.y} M ${bottomLeft.x} ${bottomLeft.y} L ${centre.x} ${centre.y}`,fill:"none",stroke:"#151922","stroke-width":"11","stroke-linecap":"round","stroke-linejoin":"round",class:"pd-fto-boundary"}));
    };

    if (puzzle === "cube") (diagram === "last-layer" ? renderLastLayer : renderCube)();
    else if (puzzle === "pyraminx") (diagram === "two-face" ? renderLegacyPyraminx : renderPyraminx)();
    else if (puzzle === "megaminx") (diagram === "last-layer" ? renderLegacyMegaminx : renderMegaminx)();
    else ({ skewb: renderSkewb, fto: renderFto }[puzzle] || (() => {}))();
    return svg;
  }

  function configFromElement(host) {
    return { puzzle: host.dataset.puzzle || "cube", diagram: host.dataset.diagram || "three-d", size: host.dataset.size || 3, dimensions: host.dataset.dimensions || "", view: host.dataset.view || "right", state: host.dataset.state || "", gap: host.dataset.gap || 6, label: host.dataset.label || "" };
  }

  function renderElement(host, onPieceClick) {
    const config = configFromElement(host), state = normaliseState(config.state);
    if (config.puzzle === "pyraminx" && state.length === 18) config.diagram = "two-face";
    if (config.puzzle === "megaminx" && state.length === 26) config.diagram = "last-layer";
    const expected = expectedCount(config);
    host.classList.add("pd-host");
    if (state.length !== expected) {
      host.replaceChildren(Object.assign(document.createElement("p"), { className: "pd-error", textContent: `Diagram needs ${expected} colours; ${state.length} supplied.` }));
      return false;
    }
    host.replaceChildren(makeSvg({ ...config, state }, onPieceClick));
    host.dataset.pdReady = "true";
    return true;
  }

  function renderAll(root = document) {
    root.querySelectorAll(".puzzle-diagram").forEach((host) => renderElement(host));
  }

  window.SpeedcubingPuzzleDiagrams = { colours: COLOURS, normaliseState, parseDimensions, expectedCount, defaultState, makeSvg, renderElement, renderAll };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => renderAll()); else renderAll();
  document.addEventListener("shopify:section:load", (event) => renderAll(event.target));
})();